import { isObject } from 'underscore';

import { EmailCalendarLinks, EmailTextPhrases, ICSSettings } from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { Session } from '@/types/session.types';
import { LanguagesAbbr } from '@/types/translation.types';
import { sessionNameAndDescription } from './session-name-and-description';
import { projectNameAndDescription } from './project-name-and-description';
import { getUrl } from './get-url';
import { googleCalendarLink, icsFile } from './calendar-invites';
import { Channel } from '@/types/channel.types';
import { GetUploadEndpoint, getUploadFilename, UploadFileToProvider } from '@ui/uploads/validations';
import { isEmailTemplateV1 } from './is-email-template-v1';

type TGetCalendarLinksParams = {
  project: BrandliveEvent;
  channel: Channel;
  publishedUrl: string;
  language: LanguagesAbbr;
  translations: EmailTextPhrases;
  session?: Session;
  icsSettings?: ICSSettings;
  batchId?: string;
};

export const getCalendarLinks = async ({
  project,
  channel,
  publishedUrl,
  language,
  translations,
  session,
  icsSettings = {},
  batchId,
}: TGetCalendarLinksParams): Promise<EmailCalendarLinks | undefined> => {
  let currentIcs;
  let currentIcsLink;
  let currentGoogleLink;
  const regConfEmail = project.registration_settings?.registration_confirmation_email?.[language];
  if (isEmailTemplateV1(regConfEmail)) {
    currentIcs = regConfEmail.icsContent;
    currentIcsLink = regConfEmail.icsLink;
    currentGoogleLink = regConfEmail.googleLink;
  }

  const start =
    session?.timestamp ??
    project.sessions
      .map((session) => session.timestamp)
      .filter((timestamp) => timestamp)
      .sort()[0];
  const end =
    session?.end_timestamp ??
    project.sessions
      .map((session) => session.end_timestamp)
      .filter((timestamp) => timestamp)
      .sort((a, b) => (b as number) - (a as number))[0];

  let googleName = '';
  let googleDescription = '';
  let othersName = '';
  let othersDescription = '';

  if (session) {
    const { name, description } = sessionNameAndDescription(session, language);
    googleName = name;
    googleDescription = description;
    othersName = name;
    othersDescription = description;
  } else {
    const googleNameAndDescription = projectNameAndDescription(project, language, 'google');
    const othersNameAndDescription = projectNameAndDescription(project, language, 'others');

    const customTitle = icsSettings?.customTitle?.[language] as string;
    const customDescription = icsSettings?.customDescription?.[language] as string;

    if (customTitle || customDescription) {
      googleName = customTitle || googleNameAndDescription.name;
      googleDescription = customDescription || googleNameAndDescription.description;
      othersName = customTitle || othersNameAndDescription.name;
      othersDescription = customDescription || othersNameAndDescription.description;
    } else {
      googleName = googleNameAndDescription.name;
      googleDescription = googleNameAndDescription.description;
      othersName = othersNameAndDescription.name;
      othersDescription = othersNameAndDescription.description;
    }
  }

  const url = getUrl(channel, project, publishedUrl, language, session?.uuid);
  const uuid = session?.uuid ?? project.uuid;

  try {
    const [googleFile, content] = icsFile(
      start,
      end,
      googleName,
      googleDescription,
      url,
      uuid,
      translations,
      !!icsSettings?.customDescription?.[language]
    );
    const [file, icsContent] = icsFile(
      start,
      end,
      othersName,
      othersDescription,
      url,
      uuid,
      translations,
      !!icsSettings?.customDescription?.[language]
    );
    const noChange = icsContent === currentIcs;

    if (
      typeof currentIcs === 'string' &&
      typeof currentIcsLink === 'string' &&
      typeof currentGoogleLink === 'string' &&
      noChange
    ) {
      return {
        iCal: currentIcsLink,
        outlook: currentIcsLink,
        google: currentGoogleLink,
        content: content,
      };
    }

    if (googleFile) {
      // await UploadFileToProvider(googleFile, undefined, { 'batch-id': 'get_calendar_links' });
      const endpoint = await GetUploadEndpoint({
        filename: getUploadFilename(googleFile.name),
        ...(batchId
          ? {
              headers: { 'batch-id': batchId },
            }
          : {}),
      });
      if (isObject(endpoint) && endpoint.url) {
        await UploadFileToProvider(
          {
            url: endpoint.url,
            fields: endpoint.fields,
            cdnHost: endpoint?.cdnHost,
          },
          googleFile
        );
      }
    }

    const googsCalendarLink = googleCalendarLink(
      start,
      end,
      googleName,
      googleDescription,
      url,
      translations,
      !!icsSettings?.customDescription?.[language]
    );
    const endpoint =
      file &&
      (await GetUploadEndpoint({
        filename: getUploadFilename(file.name),
        ...(batchId
          ? {
              headers: { 'batch-id': batchId },
            }
          : {}),
      }));
    let icsLink = '';
    if (file && isObject(endpoint) && endpoint.url) {
      icsLink = await UploadFileToProvider(
        {
          url: endpoint.url,
          fields: endpoint.fields,
          cdnHost: endpoint?.cdnHost,
        },
        file
      );
    }

    return { iCal: icsLink, outlook: icsLink, google: googsCalendarLink, content: icsContent };
  } catch (e) {
    console.error('error getting calendar links: ', e);
    return undefined;
  }
};
